import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import HoldingsBuilder from './HoldingsBuilder.jsx';
import RiskAnalytics from './RiskAnalytics.jsx';
import { portfolioApi } from '../../lib/apiClient.js';

const INITIAL_HOLDINGS = [
  { symbol: 'DANGCEM.NG', exchange: 'NGX', weight: 0.3 },
  { symbol: 'SCOM.KE', exchange: 'NSE', weight: 0.25 },
  { symbol: 'NPN.ZA', exchange: 'JSE', weight: 0.25 },
  { symbol: 'COMI.EG', exchange: 'EGX', weight: 0.2 },
];

/**
 * Portfolio construction + risk analytics. Portfolios are persisted server-
 * side (save/load/delete); risk analytics are computed live from MSA
 * adjusted OHLCV. The full engine run happens in Backtest Studio, deep-linked
 * with the constructed symbols as its starting universe.
 */
export default function PortfolioBacktest() {
  const [holdings, setHoldings] = useState(INITIAL_HOLDINGS);
  const [saved, setSaved] = useState([]);
  const [name, setName] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const navigate = useNavigate();

  const totalWeight = holdings.reduce((s, h) => s + h.weight, 0);
  const isBalanced = Math.abs(totalWeight - 1) < 0.005;
  const hasName = Boolean(name.trim());

  async function refreshSaved() {
    try {
      setSaved(await portfolioApi.list());
    } catch {
      setSaved([]);
    }
  }

  useEffect(() => {
    refreshSaved();
  }, []);

  async function savePortfolio() {
    if (!name.trim()) {
      setSaveStatus('missing-name');
      return;
    }
    if (!isBalanced || holdings.length === 0) {
      setSaveStatus('invalid-allocation');
      return;
    }
    setSaveStatus('saving');
    try {
      await portfolioApi.create({ name: name.trim(), holdings });
      setName('');
      setSaveStatus('saved');
      await refreshSaved();
    } catch {
      setSaveStatus('error');
    }
  }

  async function loadPortfolio(p) {
    setHoldings(p.holdings || []);
  }

  async function deletePortfolio(id) {
    await portfolioApi.remove(id);
    await refreshSaved();
  }

  return (
    <>
      <Card>
        <h1 className="heading" style={{ fontSize: 22 }}>
          Portfolio & Backtest
        </h1>
        <p className="aq-module-desc">
          Construct a portfolio, review risk analytics, then open Backtest Studio to run a full research-grade backtest
          on the selected symbol universe. Holdings weights are not transferred to the rules backtest. Price-adjustment basis is unverified.
        </p>
      </Card>

      <section>
        <h2 className="aq-section-title heading">Construction</h2>
        <HoldingsBuilder holdings={holdings} onChange={setHoldings} />
      </section>

      <section>
        <h2 className="aq-section-title heading">Saved portfolios</h2>
        <Card>
          {saved.length === 0 && (
            <div className="aq-empty-state">No saved portfolios yet. Name and save your construction below.</div>
          )}
          {saved.map((p) => (
            <div key={p.id} className="aq-saved-portfolio-row" style={{ marginBottom: 8 }}>
              <span className="aq-holding-symbol">{p.name}</span>
              <span className="aq-news-meta">
                {p.holdings?.length || 0} holding{(p.holdings?.length || 0) === 1 ? '' : 's'} ·{' '}
                {new Date(p.updatedAt).toLocaleDateString()}
              </span>
              <Button size="sm" variant="secondary" onClick={() => loadPortfolio(p)}>
                Load
              </Button>
              <Button size="sm" variant="ghost" className="aq-btn--danger" onClick={() => deletePortfolio(p.id)}>
                Delete
              </Button>
            </div>
          ))}
          <div className="aq-add-pos" style={{ marginTop: 12 }}>
            <input
              type="text"
              placeholder="Portfolio name"
              value={name}
              onChange={(e) => { setName(e.target.value); setSaveStatus('idle'); }}
              required
              aria-invalid={!hasName}
              style={{ width: 220 }}
            />
            <Button size="sm" onClick={savePortfolio} disabled={saveStatus === 'saving' || !hasName || !isBalanced || holdings.length === 0}>
              {saveStatus === 'saving' ? 'Saving…' : 'Save portfolio'}
            </Button>
            {saveStatus === 'saved' && <span className="aq-news-meta">Saved ✓</span>}
            {saveStatus === 'error' && <span className="aq-news-meta">Save failed — retry</span>}
            {saveStatus === 'missing-name' && <span className="aq-pf-total-warn">Portfolio name is required.</span>}
            {saveStatus === 'invalid-allocation' && <span className="aq-pf-total-warn">Allocation must equal 100%.</span>}
            {!hasName && <span className="aq-module-desc">Enter a portfolio name to enable Save.</span>}
            {!isBalanced && holdings.length > 0 && <span className="aq-pf-total-warn">Balance to 100% before saving.</span>}
          </div>
        </Card>
      </section>

      <section>
        <h2 className="aq-section-title heading">Risk analytics</h2>
        <RiskAnalytics holdings={holdings} />
      </section>

      <section>
        <h2 className="aq-section-title heading">Backtest</h2>
        <Card>
          <p className="aq-module-desc" style={{ marginBottom: 12 }}>
            Backtest Studio gives you the full open_backtester research suite — rules/indicators, signals, CPU & GPU ML,
            optimize, Monte-Carlo, walk-forward, and multi-strategy portfolio analysis. Your {holdings.length} constructed
            holding{holdings.length === 1 ? '' : 's'} will be pre-loaded as the starting universe.
          </p>
          <Button
            disabled={!isBalanced || holdings.length === 0}
            onClick={() => navigate('/app/backtest', { state: { symbols: holdings.map((h) => h.symbol) } })}
          >
            Open in Backtest Studio →
          </Button>
          {!isBalanced && holdings.length > 0 && (
            <p className="aq-note">Balance holdings to 100% allocation before backtesting.</p>
          )}
        </Card>
      </section>
    </>
  );
}
